import { useAuth } from '@/contexts/AuthContext'
import type { Module, Permission } from '@/types/rbac'
import { UserRole } from '@/types/rbac'

export interface UseRBACReturn {
  userRole: UserRole | null
  isAuthenticated: boolean
  isLoading: boolean
  hasModuleAccess: (module: Module) => boolean
  hasPermission: (module: Module, permission: Permission) => boolean
  hasActionPermission: (permission: string) => boolean
  canAccessRoute: (pathname: string) => boolean
  canManageUser: (targetRole: UserRole) => boolean
  hasRole: (role: UserRole) => boolean
  accessibleModules: Module[]
  getModulePermissions: (module: Module) => Permission[]
  isAllowed: boolean
  permissions: string[]
}

/**
 * Real RBAC hook — uses permissions from AuthContext.
 */
export function useRBAC(): UseRBACReturn {
  const { user, permissions: authPermissions, isAuthenticated, loading } = useAuth()
  const isLoading = loading
  const permissions = authPermissions || []
  const userRole = (user?.role as string)?.toUpperCase() as UserRole || null

  const hasActionPermission = (permission: string) => {
    if (userRole === UserRole.ROOT) return true

    return permissions.includes(permission)
  }

  const hasModuleAccess = (module: Module) => {
    if (userRole === UserRole.ROOT) return true

    // Check if user has any permission starting with the module name or general view permission
    const modulePrefix = module.toLowerCase()

    return permissions.some(p => p.startsWith(`view-${modulePrefix}`) || p.startsWith(`manage-${modulePrefix}`))
  }

  const hasPermission = (module: Module, permission: Permission) => {
    if (userRole === UserRole.ROOT) return true

    const permString = `${permission.toLowerCase()}-${module.toLowerCase()}`

    return permissions.includes(permString) || permissions.includes(`manage-${module.toLowerCase()}`)
  }

  return {
    userRole,
    isAuthenticated,
    isLoading,
    hasModuleAccess,
    hasPermission,
    hasActionPermission,
    canAccessRoute: () => true, // Simplification for now
    canManageUser: (targetRole: UserRole) => {
      if (userRole === UserRole.ROOT) return true
      if (userRole === UserRole.ADMIN && targetRole !== UserRole.ROOT) return true

      return false
    },
    hasRole: (role: UserRole) => userRole === role,
    accessibleModules: [],
    getModulePermissions: () => [],
    isAllowed: isAuthenticated,
    permissions
  }
}

export function usePermission(permission: string): boolean {
  const { hasActionPermission } = useRBAC()

  return hasActionPermission(permission)
}
